import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { COLUMNS, type TaskStatus, computeAlerts, inScope, localToday, matchesSearch, normalize, statusName } from '@quadro/domain';
import { Icon } from '../../components/Icon';
import { Options } from '../../components/Options';
import { useRoutines, useTasks } from '../../data/queries';
import { Page } from '../../layout/Page';
import { openSheet } from '../../state/sheets';
import { useUI } from '../../state/ui';
import { useWorkspace } from '../../state/workspace';
import { CaptureField } from '../shared/Capture';
import { useEntities } from '../shared/useMap';
import { EntityOptions } from '../orgmap/EntityOptions';
import { Board } from './Board';
import { ColorBar } from './ColorBar';
import { DayHeader } from './DayHeader';
import { InsightsPanel } from './InsightsPanel';
import { useTaskActions } from './useTaskActions';
import { hud } from '../../components/Hud';
import { exportTasksXlsx } from './exportXlsx';
import { TemplatePicker } from '../templates/TemplatePicker';

export function BoardPage() {
  const ws = useWorkspace();
  const ui = useUI();
  const tasksQ = useTasks();
  const all = tasksQ.data ?? [];
  const actions = useTaskActions();
  const scoped = useMemo(() => all.filter(t => inScope(t, ui.scope, ws.me.id)), [all, ui.scope, ws.me.id]);
  const alerts = useMemo(() => computeAlerts(scoped, { today: localToday(), wipLimit: ws.settings.wipLimit, firstName: ws.firstName }), [scoped, ws]);
  const highlight = ui.highlight ? alerts.find(a => a.id === ui.highlight) : undefined;
  const highlightIds = highlight ? new Set(highlight.taskIds) : null;
  const stickyRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (mainRef.current && stickyRef.current) mainRef.current.style.setProperty('--wb', `${stickyRef.current.offsetHeight}px`);
  });

  // Atalhos: N = nova tarefa, / = buscar; no cartão: Enter abre, Alt+←/→ muda de etapa.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const card = target.closest?.<HTMLElement>('.kcard');
      if (card && target === card) {
        const t = all.find(x => x.id === card.dataset.card);
        if (!t) return;
        if (e.altKey && (e.key === 'ArrowRight' || e.key === 'ArrowLeft') && ws.canWrite) {
          e.preventDefault();
          const i = COLUMNS.findIndex(c => c.id === t.status) + (e.key === 'ArrowRight' ? 1 : -1);
          if (i < 0 || i >= COLUMNS.length) return;
          actions.move(t.id, COLUMNS[i].id as TaskStatus);
          setTimeout(() => document.querySelector<HTMLElement>(`[data-card="${t.id}"]`)?.focus(), 80);
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSheet({ kind: 'task', id: t.id }); return; }
      }
      const typing = /INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName ?? '') || document.querySelector('.layer.open');
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if ((e.key === 'n' || e.key === 'N') && ws.canWrite) { e.preventDefault(); document.getElementById('capIn')?.focus(); }
      if (e.key === '/') { e.preventDefault(); document.getElementById('fBusca')?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [all, ws.canWrite, actions]);

  const routines = useRoutines().data ?? [];
  const ents = useEntities();
  const [exporting, setExporting] = useState(false);
  async function exportXlsx() {
    setExporting(true);
    try {
      const list = scoped.filter(t => matchesSearch(t, ui.search, normalize));
      const n = await exportTasksXlsx(list, {
        settings: ws.settings, person: ws.person, members: ws.members, entityName: ents.name,
        routineName: id => routines.find(r => r.id === id)?.title,
      }, title);
      hud(`Planilha com ${n} ${n === 1 ? 'tarefa' : 'tarefas'} salva`);
    } catch { hud('Não foi possível gerar a planilha.'); } finally { setExporting(false); }
  }

  const title = ui.scope.project === 'all' ? 'Todos os projetos' : ui.scope.project === 'mine' ? 'Minhas tarefas'
    : ws.settings.projects.find(p => p.id === ui.scope.project)?.name ?? 'Projeto';

  return (
    <Page toolbar={<>
      <div className="tb-title"><span className="crumb">{title}</span></div>
      {highlight && <span className="hl-chip">{highlight.title}<button onClick={() => ui.set({ highlight: null })} aria-label="Parar de destacar"><Icon name="x" /></button></span>}
      <div className="tb-space" />
      <div className="tools">
        <label className="search"><Icon name="search" /><input type="search" id="fBusca" placeholder="Buscar" aria-label="Buscar cartões" value={ui.search} onChange={e => ui.set({ search: e.target.value })} /></label>
        <Filters />
        <button className="tb-btn" onClick={exportXlsx} disabled={exporting || !all.length} aria-label="Exportar para Excel" title="Exportar para Excel (o que está filtrado, com as concluídas)"><Icon name="download" /></button>
      </div>
    </>}>
      <div className="qlayout">
        <div className="qmain" style={{ overflow: 'auto' }} ref={mainRef}>
          <DayHeader alerts={alerts} />
          <div className="writebar" ref={stickyRef}>
            {ws.canWrite && (
              <div className="write-row">
                <CaptureField id="capIn" placeholder="Escreva uma tarefa: título, prazo, #projeto, @pessoa" label="Nova tarefa"
                  hint="Use amanhã, sexta, #projeto, @pessoa, +tema, &entidade, !alta"
                  onSubmit={async r => { const id = await actions.createFromCapture(r, 'mapped'); if (id) hud(`Adicionada em ${statusName('mapped')}`); return !!id; }} />
                <TemplatePicker />
              </div>
            )}
            <ColorBar tasks={scoped.filter(t => matchesSearch(t, ui.search, normalize))} />
          </div>
          {tasksQ.isPending ? <Skeleton /> : (
            <>
              {!all.length && !ws.settings.projects.length && ws.canWrite && <Welcome />}
              <Board tasks={all} highlightIds={highlightIds} />
            </>
          )}
        </div>
        {ui.insights && <InsightsPanel alerts={alerts} tasks={scoped} />}
      </div>
    </Page>
  );
}

function Skeleton() {
  return (
    <div className="skel">{[5, 3, 4, 2, 3].map((n, i) => (
      <div className="sk-col" key={i} style={{ '--i': i } as React.CSSProperties}><span className="sk sk-h" />{Array.from({ length: n }, (_, k) => <span className="sk sk-c" key={k} />)}</div>
    ))}</div>
  );
}

function Welcome() {
  return (
    <div className="welcome"><strong>Comece pelo básico.</strong> Defina projetos, temas e categorias da área, mapeie as rotinas e escreva tarefas na linha acima.
      <span className="w-links">
        <button className="side-link" onClick={() => openSheet({ kind: 'settings', tab: 'projects' })}>Ajustes da área</button>
        <button className="side-link" onClick={() => openSheet({ kind: 'routine', id: null })}>Mapear uma rotina</button>
        <button className="side-link" onClick={() => openSheet({ kind: 'people' })}>Pessoas</button>
      </span>
    </div>
  );
}

function Filters() {
  const ws = useWorkspace();
  const { scope, setScope, sort, set } = useUI();
  const ents = useEntities();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const n = [scope.person !== 'all' && scope.project !== 'mine', scope.theme !== 'all', scope.category !== 'all', scope.entity !== 'all', sort !== 'manual'].filter(Boolean).length;
  const people: [string, string][] = [['all', 'Todas as pessoas'], ['me', 'Só eu'], ...ws.members.filter(p => p.id !== ws.me.id).map(p => [p.id, p.name] as [string, string]), ['nobody', 'Sem responsável']];
  return (
    <div className="filtros" ref={ref}>
      <button className={`tb-pill ${n ? 'on' : ''}`} aria-expanded={open} aria-controls="filtrosPop" onClick={() => setOpen(!open)}>
        <Icon name="filter" />Filtros{n > 0 && <span className="nbadge">{n}</span>}
      </button>
      {open && (
        <div className="pop-panel" id="filtrosPop" role="group" aria-label="Filtros do quadro">
          {scope.project !== 'mine' && <label>Responsável<select value={scope.person} onChange={e => setScope({ person: e.target.value })} autoFocus><Options items={people} /></select></label>}
          <label>Tema<select value={scope.theme} onChange={e => setScope({ theme: e.target.value })}><Options items={[['all', 'Todos os temas'], ...ws.settings.themes.map(t => [t.id, t.name] as [string, string])]} /></select></label>
          {ents.graph && <label>Entidade<select value={scope.entity} onChange={e => setScope({ entity: e.target.value })}><EntityOptions empty={['all', 'Todas as entidades']} /></select></label>}
          <label>Categoria<select value={scope.category} onChange={e => setScope({ category: e.target.value })}><Options items={[['all', 'Todas as categorias'], ...ws.settings.categories.map(c => [c.id, c.name] as [string, string])]} /></select></label>
          <label>Ordem<select value={sort} onChange={e => set({ sort: e.target.value as typeof sort })}><Options items={[['manual', 'Manual'], ['priority', 'Por prioridade'], ['due', 'Por prazo']]} /></select></label>
          {n > 0 && <button className="side-link" onClick={() => { setScope({ person: 'all', theme: 'all', category: 'all', entity: 'all' }); set({ sort: 'manual' }); }}>Limpar filtros</button>}
        </div>
      )}
    </div>
  );
}
